import { useCallback } from 'react'

import { Minus } from 'lucide-react'
import { useFormContext } from 'react-hook-form'

import { FormError } from '@fortem-monorepo/react-ui'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import { FormDescription, FormField, FormItem, FormLabel } from '@fortem-monorepo/react-ui'
import { Input } from '@fortem-monorepo/react-ui'
import { ContentBlocks } from '@fortem-monorepo/web-sdk'

import { GuidelineFormValues } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/schema'

export interface IdentifiersFieldProps {
  identifierColumns?: ContentBlocks.DataGridColumn[]
}

export const IdentifiersField: React.FC<IdentifiersFieldProps> = ({ identifierColumns = [] }) => {
  const { control, setValue, register } = useFormContext<GuidelineFormValues>()

  const columnNameMap = identifierColumns.reduce(
    (prev, curr) => {
      return {
        ...prev,
        [curr.name]: curr.label,
      }
    },
    {} as Record<string, string>,
  )

  const removeIdentifier = useCallback(
    (values: GuidelineFormValues['identifiers'], value: string) => {
      let _new: GuidelineFormValues['identifiers'] | null = Object.assign({}, values)

      _new = Object.fromEntries(Object.entries(_new).filter(([key]) => key !== value))

      if (Object.keys(_new).length === 0) {
        _new = null
      }

      setValue('identifiers', _new, { shouldValidate: true, shouldDirty: true })
    },
    [setValue],
  )

  const renameIdentifier = (
    identifiers: Record<string, string> | null,
    oldKey: string,
    newKey: string,
  ) => {
    const next = Object.fromEntries(
      Object.entries(identifiers ?? {}).map(([key, value]) =>
        key === oldKey ? [newKey, value] : [key, value],
      ),
    )

    setValue('identifiers', next, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  return (
    <FormField
      name="identifiers"
      control={control}
      render={({ field: { value } }) => {
        const entries = Object.entries(value ?? {})
        const valueSet = new Set(Object.keys(value ?? {}))
        const availableColumns = identifierColumns.filter((column) => !valueSet.has(column.name))

        return (
          <FormItem>
            <div className="flex items-center gap-2 overflow-hidden">
              <FormLabel>Identifiers</FormLabel>
              <FormError />
            </div>
            <FormDescription>
              Assign values to identifiers to target specific cells or rows.
            </FormDescription>
            <div className={'space-y-2'}>
              {entries.map(([_key]) => {
                return (
                  <div key={_key} className={'flex items-center gap-2'}>
                    <SearchSelect
                      placeholder={'Add an identifier...'}
                      value={_key}
                      options={[
                        { label: columnNameMap[_key] ?? _key, value: _key },
                        ...availableColumns.map((column) => ({
                          label: column.label,
                          value: column.name,
                        })),
                      ]}
                      setValue={(val) => renameIdentifier(value, _key, val)}
                    />
                    <Input
                      {...register(`identifiers.${_key}`)}
                      placeholder={'Enter the value to match...'}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => removeIdentifier(value, _key)}
                    >
                      <Minus />
                    </Button>
                  </div>
                )
              })}
              {availableColumns.length > 0 && (
                <div className={'flex items-center gap-2'}>
                  <SearchSelect
                    placeholder={'Add an identifier...'}
                    options={availableColumns.map((column) => ({
                      label: column.label,
                      value: column.name,
                    }))}
                    setValue={(val) => {
                      setValue(
                        'identifiers',
                        { ...value, [val]: '' },
                        { shouldValidate: true, shouldDirty: true },
                      )
                    }}
                  />
                </div>
              )}
            </div>
          </FormItem>
        )
      }}
    />
  )
}
